import React, { FC, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { PublicError } from '@modernloop/shared/helper-components';
import { Button, CircularProgress, Stack, TextField, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash';

import { SourceType, useActivityNoteCreateMutation } from 'src/generated/mloop-graphql';

import { buildPlainTextActivityLogContent } from 'src/entities/ActivityLog/Helpers/activityLogPlainText';

import { OS, getOS } from 'src/utils/browserUtils';

interface CreateNoteTextFieldProps {
  onSubmit: (note: string) => void;
  onChange: (note: string) => void;
  sourceId: string;
  sourceType: SourceType;
}

const CreateNoteTextField: FC<CreateNoteTextFieldProps> = ({ onSubmit, sourceId, sourceType, onChange }) => {
  const { t } = useTranslation();
  const [activityNoteCreateMutation, { loading, error }] = useActivityNoteCreateMutation();

  const [newNote, setNewNote] = useState<string>('');

  const cmdKOrCtrlEnter = useMemo(() => {
    if ([OS.MacOS, OS.iOS].includes(getOS())) {
      return '⌘+Enter';
    }
    return 'Ctrl+Enter';
  }, []);

  const onNewNoteSubmit = () => {
    const trimmedNote = newNote.trim();
    const content = buildPlainTextActivityLogContent(trimmedNote);

    // eslint-disable-next-line promise/catch-or-return
    activityNoteCreateMutation({
      variables: {
        input: {
          note: trimmedNote,
          content,
          sourceId,
          sourceType,
        },
      },
    }) // eslint-disable-next-line promise/always-return
      .then(() => {
        setNewNote('');
        onSubmit(trimmedNote);
      });
  };

  return (
    <Stack direction="column" spacing={1}>
      <TextField
        placeholder={t('hiring-dashboard-common:common.activityLogs.addComment')}
        multiline
        value={newNote}
        disabled={loading}
        onChange={(e) => {
          const noteText = e.target.value.trimStart();
          setNewNote(noteText);
          onChange(noteText);
        }}
        InputProps={{
          endAdornment: loading ? (
            <CircularProgress size={20} />
          ) : (
            <Tooltip
              title={
                isEmpty(newNote)
                  ? undefined
                  : t('hiring-dashboard-common:common.activityLogs.postShortcut', { shortcut: cmdKOrCtrlEnter })
              }
            >
              <Button size="small" onClick={onNewNoteSubmit} disabled={isEmpty(newNote)} color="primary">
                {t('hiring-dashboard-common:common.post')}
              </Button>
            </Tooltip>
          ),
          onKeyDown: (e) => {
            // Cmd/Ctrl+Enter always submits
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && newNote) {
              e.preventDefault();
              onNewNoteSubmit();
            }
            // Plain Enter submits (Shift+Enter for newline)
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && newNote) {
              e.preventDefault();
              onNewNoteSubmit();
            }
          },
        }}
      />
      {error ? <PublicError error={error} /> : null}
    </Stack>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ActivityNoteCreateMutation = gql`
  mutation ActivityNoteCreate($input: ActivityNoteCreateInput!) {
    activityNoteCreate(input: $input) {
      activityLog {
        id
        createdAt
        activityType
        activityActorType
        activityActorEmployeeId
        actorEmployee {
          id
          fullName
          internalProfilePictureUrl
        }
        details {
          ... on ActivityLogNoteDetails {
            note
          }
        }
      }
    }
  }
`;

export default CreateNoteTextField;
